import clsx from "clsx";
import { useCssVars } from "@/lib/cssVars";
import styles from "./Skeleton.module.css";

export interface SkeletonProps {
  /** A preset fraction of the container, or a width in characters (12 → 12ch). */
  width?: "full" | "3/4" | "1/2" | "1/4" | number;
  /** "text" matches a line of body text, "title" a heading, "control" a button or input, "block" a chart. */
  shape?: "text" | "title" | "control" | "block";
  /** Repeat as several text lines (the last one shorter). */
  lines?: number;
  className?: string;
}

/**
 * A placeholder shaped like the content that is loading. Show it only after ~150 ms
 * (useDelayedVisibility) so fast responses never flash. Hidden from assistive technology;
 * announce loading with aria-busy on the region instead.
 */
export function Skeleton({ width = "full", shape = "text", lines = 1, className }: SkeletonProps) {
  const widthRef = useCssVars<HTMLSpanElement>({
    "--skeleton-width": typeof width === "number" ? `${width}ch` : null,
  });
  if (lines > 1) {
    return (
      <span className={clsx(styles.lines, className)} aria-hidden="true">
        {Array.from({ length: lines }, (_, index) => (
          <span
            key={index}
            className={styles.skeleton}
            data-shape="text"
            data-width={index === lines - 1 ? "3/4" : "full"}
          />
        ))}
      </span>
    );
  }
  return (
    <span
      ref={widthRef}
      className={clsx(styles.skeleton, className)}
      data-shape={shape}
      data-width={typeof width === "number" ? "custom" : width}
      aria-hidden="true"
    />
  );
}
